import { gameOverlayPause } from '../game-overlay-pause';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import {
  EyeOff,
  Settings2,
  FileText,
  ChevronLeft,
  MoreHorizontal,
  Check,
  Globe2,
  Keyboard,
  ChevronUp,
} from 'lucide-react';
import { Modal } from './Modal';
import { WorkCoverPage } from './WorkCoverPage';
import { usePlatform } from '../store';
import {
  DEFAULT_PRIVACY,
  COVER_PAGES,
  WEBSITE_SHORTCUTS,
  PRIVACY_EVENT,
  PRIVACY_HIDDEN_KEY,
  PRIVACY_SETTINGS_KEY,
  matchesPrivacyKey,
  parsePrivacySettings,
  privacyHidden,
  safeCoverUrl,
  shortcutLabel,
  type PrivacySettings,
  privacyTabIdentity,
  parseWebsiteTab,
  safeTabIcon,
  fetchWebsiteTab,
  shortcutFromEvent,
} from '../privacy';
import './privacy.css';
import { createGamePauseController } from '../game-pause';

type GameFrame = {
  element: HTMLIFrameElement;
  origin: string;
  pause: ReturnType<typeof createGamePauseController>;
};
type PrivacyContext = {
  settings: PrivacySettings;
  activate: () => void;
  configure: () => void;
  registerFrame: (element: HTMLIFrameElement, origin: string) => () => void;
};
const Context = createContext<PrivacyContext>(null!);
const NOTES_KEY = 'moyu:privacy:notes:v1';
const initialNotes =
  '本周工作安排\n\n一、待处理事项\n整理项目资料，更新任务进度。\n确认下一阶段的时间安排。\n\n二、会议记录\n\n\n三、后续跟进\n';
const read = (key: string, fallback = '') => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const { notify } = usePlatform();
  const [settings, setSettings] = useState(() => parsePrivacySettings(read(PRIVACY_SETTINGS_KEY)));
  const [hidden, setHidden] = useState(privacyHidden);
  const [configuring, setConfiguring] = useState(false);
  const [notes, setNotes] = useState(() => read(NOTES_KEY, initialNotes));
  const [noteSaved, setNoteSaved] = useState(true);
  const [notesFallback, setNotesFallback] = useState(false);
  const externalCover = settings.mode === 'webpage' && settings.page === 'custom' && !notesFallback;
  const hideToolbar = externalCover && settings.autoHideToolbar !== false;
  const webpageCover = settings.mode === 'webpage' && !notesFallback;
  const frames = useRef(new Set<GameFrame>());
  const latest = useRef({ settings, hidden, configuring });
  latest.current = { settings, hidden, configuring };
  const dialog = useRef<HTMLDialogElement>(null);
  const send = useCallback((entry: GameFrame) => {
    // Panels pause the game without pretending the document is hidden.
    entry.pause.send(latest.current.hidden, document.hidden, latest.current.settings.shortcut, gameOverlayPause.active());
  }, []);
  useEffect(() => gameOverlayPause.subscribe(() => frames.current.forEach(send)), [send]);
  const changeHidden = useCallback(
    (value: boolean) => {
      if (latest.current.hidden === value) return;
      latest.current.hidden = value;
      if (value) document.documentElement.dataset.privacy = 'hidden';
      else delete document.documentElement.dataset.privacy;
      try {
        if (value) sessionStorage.setItem(PRIVACY_HIDDEN_KEY, '1');
        else sessionStorage.removeItem(PRIVACY_HIDDEN_KEY);
      } catch {
        /* In-memory cover still works. */
      }
      frames.current.forEach(send);
      flushSync(() => {
        if (value) setNotesFallback(false);
        setHidden(value);
        setConfiguring(false);
      });
      window.dispatchEvent(new Event(PRIVACY_EVENT));
      if (value) {
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
        if (document.pointerLockElement) document.exitPointerLock();
      } else {
        frames.current.values().next().value?.element.focus();
      }
    },
    [send],
  );
  const activate = useCallback(() => {
    changeHidden(!latest.current.hidden);
  }, [changeHidden]);
  const registerFrame = useCallback(
    (element: HTMLIFrameElement, origin: string) => {
      const entry = {
        element,
        origin,
        pause: createGamePauseController(
          (data) => element.contentWindow?.postMessage(data, origin),
          (state) => {
            element.dataset.pauseMode = state.mode;
            element.dataset.gamePaused = String(state.gamePaused ?? state.paused);
            element.dispatchEvent(new CustomEvent('moyu:pause-state', { detail: state }));
          },
        ),
      };
      frames.current.add(entry);
      const loaded = () => send(entry);
      element.addEventListener('load', loaded);
      send(entry);
      return () => {
        element.removeEventListener('load', loaded);
        frames.current.delete(entry);
        entry.pause.dispose();
      };
    },
    [send],
  );
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (latest.current.configuring) return;
      const target = e.target as HTMLElement | null;
      const editing = !!target?.closest('input,textarea,select,[contenteditable="true"]');
      if (!matchesPrivacyKey(e, latest.current.settings.shortcut, editing)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      activate();
    };
    const message = (e: MessageEvent) => {
      const entry = [...frames.current].find(
        (item) => e.source === item.element.contentWindow && e.origin === item.origin,
      );
      if (!entry || !e.data || typeof e.data !== 'object') return;
      if (e.data.type === 'MOYU_PRIVACY_READY') send(entry);
      if (e.data.type === 'MOYU_GAME_READY' && e.data.version === 1) send(entry);
      if (e.data.type === 'MOYU_GAME_STATE') entry.pause.receive(e.data);
      if (e.data.type === 'MOYU_PRIVACY_ACTIVATE') activate();
    };
    const visibility = () => frames.current.forEach(send);
    window.addEventListener('keydown', key, true);
    window.addEventListener('message', message);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('message', message);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [activate, send]);
  useEffect(() => {
    frames.current.forEach(send);
    try {
      localStorage.setItem(PRIVACY_SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* Settings still apply to the current document. */
    }
  }, [settings, send]);
  useEffect(() => {
    const url = safeCoverUrl(settings.url);
    if (
      settings.mode !== 'webpage' ||
      settings.page !== 'custom' ||
      !url?.startsWith('https:') ||
      WEBSITE_SHORTCUTS.some((site) => site.url === url) ||
      parseWebsiteTab(settings.tab, url)
    )
      return;
    const abort = new AbortController();
    void fetchWebsiteTab(url, abort.signal)
      .then((tab) => {
        if (abort.signal.aborted) return;
        setSettings((current) =>
          safeCoverUrl(current.url) === url && !parseWebsiteTab(current.tab, url)
            ? { ...current, tab }
            : current,
        );
      })
      .catch(() => {
        /* The hostname/favicon fallback and manual controls remain available. */
      });
    return () => abort.abort();
  }, [settings.mode, settings.page, settings.url]);
  useEffect(() => {
    try {
      localStorage.setItem(NOTES_KEY, notes);
      setNoteSaved(true);
    } catch {
      setNoteSaved(false);
    }
  }, []);
  useLayoutEffect(() => {
    if (!hidden) return;
    const el = dialog.current;
    el?.showModal();
    return () => el?.close();
  }, [hidden]);
  const tabIdentity = privacyTabIdentity(settings, notesFallback);
  useLayoutEffect(() => {
    if (hidden) window.MoyuPrivacyTab?.cover(tabIdentity);
    else window.MoyuPrivacyTab?.restore();
  }, [hidden, tabIdentity.title, tabIdentity.icon]);
  useEffect(() => () => window.MoyuPrivacyTab?.restore(), []);
  const updateNotes = (value: string) => {
    setNotes(value);
    try {
      localStorage.setItem(NOTES_KEY, value);
      setNoteSaved(true);
    } catch {
      setNoteSaved(false);
    }
  };
  return (
    <Context.Provider
      value={{
        settings,
        activate,
        configure: () => setConfiguring(true),
        registerFrame,
      }}
    >
      {children}
      {hidden &&
        createPortal(
          <dialog
            ref={dialog}
            className={`work-cover ${hideToolbar ? 'work-cover-immersive' : ''}`}
            aria-label={webpageCover ? '工作网页' : '工作笔记'}
            onCancel={(e) => e.preventDefault()}
          >
            <CoverToolbar autoHide={hideToolbar}>
              <div className="work-cover-brand">
                <FileText size={24} />
                <b>工作台</b>
                <span>/</span>
                <span>
                  {webpageCover
                    ? COVER_PAGES.find((page) => page.id === settings.page)?.title
                    : '我的文档'}
                </span>
              </div>
              <div className="work-cover-actions">
                {webpageCover && (
                  <button
                    type="button"
                    className="work-cover-return"
                    onClick={() => setNotesFallback(true)}
                  >
                    <FileText size={15} />
                    工作笔记
                  </button>
                )}
                <button
                  className="work-cover-return"
                  onClick={() => changeHidden(false)}
                  title={`返回 · ${shortcutLabel(settings.shortcut)}`}
                  aria-label="返回上一页"
                >
                  <ChevronLeft size={17} />
                  返回
                </button>
              </div>
            </CoverToolbar>
            {webpageCover && settings.page !== 'custom' ? (
              <WorkCoverPage
                page={settings.page}
                notes={notes}
                onNotesChange={updateNotes}
                noteSaved={noteSaved}
              />
            ) : webpageCover ? (
              <section className="work-webpage">
                <iframe
                  className="work-webpage-frame"
                  src={settings.url}
                  title="覆盖网页"
                  sandbox="allow-scripts allow-forms"
                  referrerPolicy="no-referrer"
                  allow="autoplay 'none'; fullscreen 'none'; camera 'none'; microphone 'none'; geolocation 'none'"
                />
              </section>
            ) : (
              <div className="work-cover-layout">
                <aside className="work-cover-sidebar">
                  <small>个人空间</small>
                  <div className="work-cover-folder">
                    <FileText size={16} />
                    工作笔记
                  </div>
                  <span>最近编辑</span>
                  <p>项目工作记录</p>
                </aside>
                <main className="work-cover-main">
                  <div className="work-document-toolbar">
                    <span>项目工作记录</span>
                    <small>
                      {noteSaved ? (
                        <>
                          <Check size={13} />
                          已保存在本机
                        </>
                      ) : (
                        '暂未保存，请保留页面'
                      )}
                      <MoreHorizontal size={18} />
                    </small>
                  </div>
                  <div className="work-document-tools" aria-hidden="true">
                    <span>正文</span>
                    <i />
                    <b>B</b>
                    <em>I</em>
                    <span>≡</span>
                    <i />
                    <span>100%</span>
                  </div>
                  <textarea
                    autoFocus
                    aria-label="工作笔记正文"
                    className="work-document"
                    maxLength={20000}
                    spellCheck={false}
                    value={notes}
                    onChange={(e) => updateNotes(e.target.value)}
                  />
                </main>
              </div>
            )}
          </dialog>,
          document.body,
        )}
      {configuring && !hidden && (
        <PrivacySettingsModal
          settings={settings}
          onClose={() => setConfiguring(false)}
          onSave={(next) => {
            setSettings(next);
            latest.current.settings = next;
            try {
              localStorage.setItem(PRIVACY_SETTINGS_KEY, JSON.stringify(next));
            } catch {
              notify('设置已在本页生效，但浏览器未允许保存，刷新后将恢复默认');
            }
            setConfiguring(false);
          }}
        />
      )}
    </Context.Provider>
  );
}

function CoverToolbar({ autoHide, children }: { autoHide: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const tools = useRef<HTMLDivElement>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const close = () => {
    clearTimeout(idleTimer.current);
    setOpen(false);
  };
  const keepOpen = () => {
    clearTimeout(idleTimer.current);
    // Some browsers omit pointerleave when entering a cross-origin iframe.
    idleTimer.current = setTimeout(() => {
      if (!tools.current?.querySelector('header')?.contains(document.activeElement)) setOpen(false);
      else keepOpen();
    }, 2200);
  };
  useEffect(() => () => clearTimeout(idleTimer.current), []);
  useEffect(() => {
    if (!autoHide) return;
    let blurTimer: ReturnType<typeof setTimeout> | undefined;
    const frameFocus = () => {
      clearTimeout(blurTimer);
      blurTimer = setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME') close();
      }, 0);
    };
    window.addEventListener('blur', frameFocus);
    return () => {
      window.removeEventListener('blur', frameFocus);
      clearTimeout(blurTimer);
    };
  }, [autoHide]);
  const visible = !autoHide || open;
  return (
    <div
      ref={tools}
      className={`work-cover-tools ${autoHide ? 'is-floating' : ''} ${visible ? 'is-open' : ''}`}
      onPointerLeave={(e) => {
        if (
          e.pointerType === 'mouse' &&
          !tools.current?.querySelector('header')?.contains(document.activeElement)
        )
          close();
      }}
    >
      {autoHide && (
        <span
          className="work-toolbar-edge"
          aria-hidden="true"
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') {
              setOpen(true);
              keepOpen();
            }
          }}
        />
      )}
      {autoHide && (
        <button
          type="button"
          className="work-toolbar-handle"
          aria-label="显示网页工具栏"
          aria-expanded={visible}
          title="显示返回按钮"
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') {
              setOpen(true);
              keepOpen();
            }
          }}
          onClick={() => setOpen(true)}
        >
          <span />
        </button>
      )}
      <header
        className="work-cover-header"
        inert={!visible}
        onPointerMove={(e) => {
          if (autoHide && e.pointerType === 'mouse') keepOpen();
        }}
      >
        {children}
        {autoHide && (
          <button
            type="button"
            className="work-toolbar-collapse"
            aria-label="收起网页工具栏"
            title="收起工具栏"
            onClick={(e) => {
              e.currentTarget.blur();
              close();
            }}
          >
            <ChevronUp size={18} />
          </button>
        )}
      </header>
    </div>
  );
}

function ShortcutRecorder({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [hint, setHint] = useState('');
  return (
    <>
      <button
        type="button"
        className={`privacy-shortcut-recorder ${recording ? 'is-recording' : ''}`}
        aria-label="自定义老板键"
        aria-describedby="privacy-shortcut-label privacy-shortcut-status"
        aria-pressed={recording}
        onClick={() => {
          setRecording(true);
          setHint('请按下快捷键，Esc 取消');
        }}
        onBlur={() => {
          setRecording(false);
          setHint('');
        }}
        onKeyDown={(e) => {
          if (!recording) return;
          e.preventDefault();
          e.stopPropagation();
          if (e.code === 'Escape') {
            setRecording(false);
            setHint('已取消修改');
            return;
          }
          if (
            /^(Control|Shift|Alt|Meta)(Left|Right)$/.test(e.code) ||
            e.repeat ||
            e.nativeEvent.isComposing
          )
            return;
          const next = shortcutFromEvent(e.nativeEvent);
          if (!next) {
            setHint('这个按键由浏览器或系统占用，请换一个');
            return;
          }
          onChange(next);
          setRecording(false);
          setHint(`已选 ${shortcutLabel(next)}，保存后生效`);
        }}
      >
        <Keyboard size={17} />
        <kbd>{recording ? '按下快捷键…' : shortcutLabel(value)}</kbd>
        <small>{recording ? '录入中' : '点击修改'}</small>
      </button>
      <span className="privacy-shortcut-status" id="privacy-shortcut-status" role="status">
        {hint}
      </span>
    </>
  );
}

function PrivacySettingsModal({
  settings,
  onClose,
  onSave,
}: {
  settings: PrivacySettings;
  onClose: () => void;
  onSave: (value: PrivacySettings) => void;
}) {
  const [draft, setDraft] = useState(settings);
  const [error, setError] = useState('');
  const custom = draft.mode === 'webpage' && draft.page === 'custom';
  const [metadataStatus, setMetadataStatus] = useState('idle');
  const preview = privacyTabIdentity(draft);
  useEffect(() => {
    const url = safeCoverUrl(draft.url);
    if (
      !custom ||
      !url?.startsWith('https:') ||
      WEBSITE_SHORTCUTS.some((site) => site.url === url) ||
      parseWebsiteTab(draft.tab, url)
    ) {
      setMetadataStatus('idle');
      return;
    }
    const abort = new AbortController();
    setMetadataStatus('loading');
    const timer = setTimeout(() => {
      void fetchWebsiteTab(url, abort.signal)
        .then((tab) => {
          if (abort.signal.aborted) return;
          setDraft((current) =>
            safeCoverUrl(current.url) === url && !parseWebsiteTab(current.tab, url)
              ? { ...current, tab }
              : current,
          );
          setMetadataStatus('loaded');
        })
        .catch(() => {
          if (!abort.signal.aborted) setMetadataStatus('failed');
        });
    }, 500);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [custom, draft.url]);
  return (
    <Modal title="老板键设置" onClose={onClose} className="privacy-dialog">
      <form
        className="privacy-settings"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const url = safeCoverUrl(draft.url);
          if (custom && !url) {
            setError('请填写完整的网站地址，例如 https://example.com');
            return;
          }
          if (custom && location.protocol === 'https:' && url?.startsWith('http:')) {
            setError('请使用 https:// 开头的网址');
            return;
          }
          if (
            custom &&
            draft.tab &&
            safeCoverUrl(draft.tab.url) === url &&
            (!draft.tab.title.trim() || !safeTabIcon(draft.tab.icon))
          ) {
            setError('请填写标签标题，并使用有效的 HTTPS 图标地址');
            return;
          }
          onSave({
            ...draft,
            url: url || '',
            tab: parseWebsiteTab(draft.tab, url || '') || undefined,
          });
        }}
      >
        <div className="privacy-settings-body">
          <div className="privacy-intro">
            <span className="privacy-intro-icon">
              <EyeOff size={23} />
            </span>
            <div>
              <h3>一键切换，随时回来</h3>
              <p>保留当前游戏，点击“返回”继续。</p>
            </div>
            <kbd>{shortcutLabel(draft.shortcut)}</kbd>
          </div>
          <div className="privacy-settings-row">
            <div className="field privacy-shortcut-field">
              <span id="privacy-shortcut-label">老板键快捷键</span>
              <ShortcutRecorder
                value={draft.shortcut}
                onChange={(shortcut) => setDraft({ ...draft, shortcut })}
              />
              <div className="privacy-shortcut-presets" aria-label="常用快捷键">
                {['F8', 'F9', 'Backquote'].map((key) => (
                  <button
                    type="button"
                    key={key}
                    aria-pressed={draft.shortcut === key}
                    onClick={() => setDraft({ ...draft, shortcut: key })}
                  >
                    {shortcutLabel(key)}
                  </button>
                ))}
              </div>
            </div>
            <label className="field">
              切换方式
              <select
                value={draft.mode}
                onChange={(e) => {
                  setDraft({ ...draft, mode: e.target.value as PrivacySettings['mode'] });
                  setError('');
                }}
              >
                <option value="notes">工作笔记</option>
                <option value="webpage">网页覆盖</option>
              </select>
            </label>
          </div>
          <p className="privacy-shortcut-help">
            点击快捷键后按下单键或组合键，例如 F8、B、Ctrl + Alt +
            B。输入文字时不触发单字母键，部分键盘的 F 键需配合 Fn。
          </p>
          {draft.mode === 'webpage' ? (
            <>
              <label className="privacy-toolbar-setting">
                <input
                  type="checkbox"
                  checked={draft.autoHideToolbar !== false}
                  onChange={(e) => setDraft({ ...draft, autoHideToolbar: e.target.checked })}
                />
                <span>
                  <b>自动隐藏网页工具栏</b>
                  <small>
                    外部网页铺满页面。鼠标移到顶部或点击小把手，可展开返回按钮；取消勾选则始终显示。
                  </small>
                </span>
              </label>
              <fieldset className="privacy-page-picker privacy-website-picker">
                <legend>常用网站</legend>
                <p>点击即可选择，自动填写地址，无需手动输入。</p>
                <div className="privacy-website-grid">
                  {WEBSITE_SHORTCUTS.map((site) => {
                    const selected = custom && safeCoverUrl(draft.url) === site.url;
                    return (
                      <button
                        key={site.url}
                        type="button"
                        className={`privacy-website-option ${selected ? 'selected' : ''}`}
                        aria-label={site.title}
                        aria-pressed={selected}
                        onClick={() => {
                          setDraft({ ...draft, page: 'custom', url: site.url, tab: undefined });
                          setError('');
                        }}
                      >
                        <Globe2 size={20} aria-hidden="true" />
                        <span>
                          <b>{site.title}</b>
                          <small>{site.domain}</small>
                        </span>
                        {selected && <Check size={16} aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
              <fieldset className="privacy-page-picker">
                <legend>内置页面与自定义</legend>
                <p>也可以使用内置工作页面，或填写其他网站地址。</p>
                <div className="privacy-page-grid">
                  {COVER_PAGES.map((page) => (
                    <label
                      key={page.id}
                      className={`privacy-page-option ${draft.page === page.id ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="cover-page"
                        value={page.id}
                        checked={draft.page === page.id}
                        onChange={() => {
                          setDraft({ ...draft, page: page.id });
                          setError('');
                        }}
                      />
                      <span
                        className={`privacy-page-preview privacy-preview-${page.id}`}
                        aria-hidden="true"
                      >
                        {page.id === 'custom' ? (
                          <Globe2 size={30} />
                        ) : (
                          <>
                            <i />
                            <i />
                            <i />
                            <i />
                            <i />
                            <i />
                          </>
                        )}
                      </span>
                      <span className="privacy-page-copy">
                        <b>{page.title}</b>
                        <small>{page.description}</small>
                      </span>
                      <span className="privacy-page-check" aria-hidden="true">
                        {draft.page === page.id && <Check size={13} />}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              {custom ? (
                <div className="privacy-custom-address">
                  <label className="field" htmlFor="privacy-cover-url">
                    覆盖网页地址
                  </label>
                  <input
                    id="privacy-cover-url"
                    type="url"
                    inputMode="url"
                    autoComplete="url"
                    value={draft.url}
                    placeholder="https://example.com"
                    maxLength={2048}
                    aria-invalid={!!error}
                    aria-describedby="privacy-url-help privacy-url-error"
                    onChange={(e) => {
                      setDraft({ ...draft, url: e.target.value });
                      setError('');
                    }}
                  />
                  <p id="privacy-url-help">
                    部分网站不允许嵌入，无法显示时可切换工作笔记。操作外部网页后，将鼠标移到顶边或点击顶部小把手，即可展开“返回”按钮。
                  </p>
                  <div className="privacy-tab-preview" aria-label="标签页预览">
                    <img
                      key={preview.icon}
                      src={preview.icon}
                      alt=""
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        if (!e.currentTarget.src.endsWith('/work-note.svg'))
                          e.currentTarget.src = '/work-note.svg';
                      }}
                    />
                    <span>{preview.title}</span>
                    <small>标签页预览</small>
                  </div>
                  <p className="privacy-tab-status" role="status">
                    {metadataStatus === 'loading'
                      ? '正在读取网站标题和图标…'
                      : metadataStatus === 'failed'
                        ? '未能自动读取，可在下方填写标签信息。'
                        : '隐藏时使用此标题和图标，返回后恢复原标签。'}
                  </p>
                  <details className="privacy-tab-custom">
                    <summary>调整标签标题与图标</summary>
                    <label className="field">
                      标签页标题
                      <input
                        maxLength={160}
                        value={
                          draft.tab?.url === safeCoverUrl(draft.url)
                            ? draft.tab.title
                            : preview.title
                        }
                        onChange={(e) => {
                          setDraft({
                            ...draft,
                            tab: {
                              url: safeCoverUrl(draft.url) || '',
                              ...preview,
                              title: e.target.value,
                            },
                          });
                          setError('');
                        }}
                      />
                    </label>
                    <label className="field">
                      标签图标地址
                      <input
                        maxLength={2048}
                        value={
                          draft.tab?.url === safeCoverUrl(draft.url) ? draft.tab.icon : preview.icon
                        }
                        onChange={(e) => {
                          setDraft({
                            ...draft,
                            tab: {
                              url: safeCoverUrl(draft.url) || '',
                              ...preview,
                              icon: e.target.value,
                            },
                          });
                          setError('');
                        }}
                      />
                    </label>
                  </details>
                  <p
                    id="privacy-url-error"
                    role={error ? 'alert' : undefined}
                    className="privacy-error"
                  >
                    {error}
                  </p>
                </div>
              ) : (
                <div className="privacy-explanation">
                  <Check size={17} />
                  <span>
                    内置页面支持快捷键返回，不会离开当前游戏。项目文档的修改仅保存在本机。
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="privacy-explanation">
              <FileText size={20} />
              <span>
                立即显示可编辑的工作笔记，并切换标签页标题与图标。再按快捷键或点击“返回”恢复。
              </span>
            </div>
          )}
        </div>
        <div className="privacy-settings-footer">
          <span>设置仅保存在此浏览器</span>
          <div className="privacy-actions">
            <button
              type="button"
              className="button ghost"
              onClick={() => {
                setDraft({ ...DEFAULT_PRIVACY });
                setError('');
              }}
            >
              恢复默认
            </button>
            <button className="button primary" type="submit">
              保存设置
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
export const usePrivacy = () => useContext(Context);
export function PrivacyControls() {
  const { settings, activate, configure } = usePrivacy();
  return (
    <div className="privacy-controls">
      <button
        className="icon-button privacy-trigger"
        onClick={activate}
        aria-label="老板键：隐藏当前页面"
        title={`隐藏当前页面 · ${shortcutLabel(settings.shortcut)}`}
      >
        <EyeOff size={18} />
        <kbd>{shortcutLabel(settings.shortcut)}</kbd>
      </button>
      <button
        className="icon-button privacy-config"
        onClick={configure}
        aria-label="老板键设置"
        title="老板键设置"
      >
        <Settings2 size={16} />
      </button>
    </div>
  );
}
